import Settings from './Settings';
import Grid from '../gui/Grid';
import Storage from './Storage';
import View from '../gui/View';
import Spinner from '../gui/Spinner';
import Selection from '../shared/Selection';
import ZoomMenu from '../gui/ZoomMenu';
import Menu from '../gui/Menu';
import ToolCollection from '../tools/edit/ToolCollection';
import SideBar from '../sidebar/edit/SideBar';
import ElementCollection from '../shapes/ElementCollection';
import DataHandler from './DataHandler';
import { updateUnitSystem } from '../shared/utils';
import Cursor from '../gui/Cursor';
import PlantRow from '../sidebar/edit/PlantRow';
import Modal from '../gui/Modal';
import HoverSet from '../shared/HoverSet';
import SpeciesCollection from '../shared/SpeciesCollection';
import SpeciesInfo from '../sidebar/SpeciesInfo';
import ShapeFactory from '../shapes/ShapeFactory';
import DrawMenu from '../drawing/DrawMenu';
import TipBar from './TipBar';
import ImageMenu from '../gui/ImageMenu';
import ActionHandler from '../tools/actions/ActionHandler';
import DeviceDetector from "device-detector-js";

export default class ServiceProvider {
    listeners = {};

    constructor(p) {
        this.p = p;
    }

    init() {
        this.initValues();
        this.initInterface();
    }

    initValues() {
        this.mode = 'edit';
        this.deviceDetector = new DeviceDetector();
        this.device = this.deviceDetector.parse(navigator.userAgent);
        this.mobile = this.device.device.type === 'smartphone';
        this.touch = ['tablet', 'smartphone'].includes(this.device.device.type);
        this.planName = null;
        this.backgroundColor = this.p.color(240);
        this.enableHotKeys = true;
        this.spinner = new Spinner(this);
        this.selection = new Selection(this);
        this.hoverSet = new HoverSet(this);
        this.units = [];
        this.unsavedChanges = false;
        this.cursor = new Cursor(this);
        this.elementFactory = new ShapeFactory(this);
        this.actions = new ActionHandler(this);
        this.image = null;
        this.loaded = false;
        this.debug = {};
        this.mouseX = 0;
        this.mouseY = 0;
    }

    initInterface() {
        this.storage = new Storage(this);
        this.dataHandler = new DataHandler(this);

        this.settings = new Settings(this);
        this.sideBar = new SideBar(this);
        this.sideBar.init();

        this.speciesInfo = new SpeciesInfo(this);

        this.zoomMenu = new ZoomMenu(this);
        this.zoomMenu.init();

        this.menu = new Menu(this);
        this.menu.init(
            ['select', 'shapes', 'navigation', 'erase', 'image'],
            ['settings', 'help', 'reset', 'save', 'undo', 'redo'],
        );

        this.drawMenu = new DrawMenu(this);
        this.imageMenu = new ImageMenu(this);

        this.tools = new ToolCollection(this);
        this.tools.init();

        this.grid = new Grid(this);

        this.view = new View(this);
        this.grid.recalculate();

        this.speciesCollection = new SpeciesCollection(this);
        this.elements = new ElementCollection(this);

        this.sideBar.activate();

        this.tips = new TipBar(this);

        this.dataHandler.getData();

        updateUnitSystem(this);
    }

    changesMade(silent = false) {
        if (!silent) {
            clearTimeout(this.saving);
            this.saving = setTimeout(() => this.storage.save(), 200);
        }
        if (!this.unsavedChanges) {
            this.unsavedChanges = true;
            window['saveButton'].style('color', '#ff0000');
        }
    }

    changesSaved() {
        this.unsavedChanges = false;
        window['saveButton'].html('Save');
        window['saveButton'].style('color', '#000000');
    }

    savePlan() {
        this.dataHandler.savePlan();
    }

    newPlantRow(species, container) {
        return new PlantRow(this, species, container);
    }

    emit(event, data) {
        this.listeners[event].forEach(listener => listener(data));
    }

    on(event, listener) {
        if (!this.listeners[event]) {
            this.listeners[event] = [];
        }
        this.listeners[event].push(listener);
    }

    showWelcomeMessage() {
        let welcomeMessage = new Modal(this);
        welcomeMessage
            .onClickOutside(() => welcomeMessage.remove())
            .setTitle('Welcome')
            .addContent(this.p.createP(
                "Welcome to the garden planner! This is an early version, so please be patient with us as we work out the kinks and expand features."
            ))
            .addContent(this.p.createP(
                "To add plants, use the sidebar on the right to search the database for plants or select them from your lists. There, you will also find details about the plants and an overview of all plants in your garden plan."
            ))
            .addContent(this.p.createP(
                "You can also draw shapes and you can add plants to those! Multiple! This allows you to draw garden beds, for example, without having to place each individual plant."
            ))
            .addContent(this.p.createP(
                "Use the menu in the topleft to add shapes, navigate, erase, change settings or find all the hotkeys available to you. You can also add a background image, which could be a satellite image of your garden, for example."
            ))
            .addContent(this.p.createP(
                "If you have any questions or any feedback at all, please <a href='mailto:hello@permapeople.org' target='_blank'>email us</a> or join us on <a href='https://discord.gg/t6PP5BsJR2' target='_blank'>discord</a>."
            ))
            .setButtonText('Get started!')
            .onClickButton(() => welcomeMessage.remove());
    }

    showMigrationMessage() {
        let migrationMessage = new Modal(this);
        migrationMessage
            .onClickOutside(() => migrationMessage.remove())
            .setTitle('It\'s been a while!')
            .addContent(this.p.createP(
                'It\'s been a long time since a big update, but we\'re back with some new features for you!'
            ))
            .addContent(this.p.createP(
                "The first big feature you'll notice is the rethinking of the labels given to shapes. They are now linked to the species of the shape, you can select which values of the species you'd like to show while also being able to add any custom labels. The reordering of labels is easy now, you can just drag them up and down."
            ))
            .addContent(this.p.createP(
                "The second big feature is the ability to undo and redo your actions. This is a big step towards making the planner more user-friendly and we hope you'll enjoy it."
            ))
            .addContent(this.p.createP(
                "And as always, if you have any questions or any feedback at all, please <a href='mailto:hello@permapeople.org' target='_blank'>email us</a> or join us on <a href='https://discord.gg/t6PP5BsJR2' target='_blank'>discord</a>."
            ))
            .setButtonText('Explore what\'s new!')
            .onClickButton(() => migrationMessage.remove());
    }
}
